import { brand, scales } from "./palette.js";
/**
 * Tokens semánticos (nombres compatibles con shadcn/ui más estados y superficie inversa).
 * Cada uno se publica como variable CSS `--<token>` y como color de Tailwind `bg-<token>`, `text-<token>`…
 */
export const SEMANTIC_TOKENS = [
    'background',
    'foreground',
    'card',
    'card-foreground',
    'popover',
    'popover-foreground',
    'primary',
    'primary-foreground',
    'secondary',
    'secondary-foreground',
    'muted',
    'muted-foreground',
    'accent',
    'accent-foreground',
    'highlight',
    'highlight-foreground',
    'highlight-soft',
    'highlight-soft-foreground',
    'destructive',
    'destructive-foreground',
    'success',
    'success-foreground',
    'success-soft',
    'success-soft-foreground',
    'warning',
    'warning-foreground',
    'warning-soft',
    'warning-soft-foreground',
    'danger-soft',
    'danger-soft-foreground',
    'inverse',
    'inverse-foreground',
    'inverse-muted-foreground',
    'inverse-accent',
    'border',
    'input',
    'ring',
];
export const PAGUZ_FONTS = {
    heading: "'Fredoka', 'Inter Variable', ui-rounded, system-ui, sans-serif",
    body: "'Inter Variable', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
};
const { navy, violet, slate, success, warning, danger } = scales;
/** Tema por defecto: Paguz. */
export const paguzTheme = {
    name: 'paguz',
    radius: '0.75rem',
    fonts: PAGUZ_FONTS,
    colors: {
        background: brand.white,
        foreground: brand.ink,
        card: brand.white,
        'card-foreground': brand.ink,
        popover: brand.white,
        'popover-foreground': brand.ink,
        primary: brand.navy,
        'primary-foreground': brand.white,
        secondary: navy[50],
        'secondary-foreground': brand.navy,
        muted: slate[50],
        'muted-foreground': slate[700],
        accent: violet[50],
        'accent-foreground': brand.navy,
        highlight: brand.violet,
        'highlight-foreground': brand.white,
        'highlight-soft': violet[100],
        'highlight-soft-foreground': violet[800],
        destructive: danger[600],
        'destructive-foreground': brand.white,
        success: success[700],
        'success-foreground': brand.white,
        'success-soft': success[50],
        'success-soft-foreground': success[800],
        warning: warning[600],
        'warning-foreground': brand.white,
        'warning-soft': warning[50],
        'warning-soft-foreground': warning[800],
        'danger-soft': danger[50],
        'danger-soft-foreground': danger[700],
        inverse: brand.navy,
        'inverse-foreground': brand.white,
        'inverse-muted-foreground': navy[200],
        'inverse-accent': brand.violetLight,
        border: slate[200],
        input: slate[500],
        ring: brand.violet,
    },
};
/** Combinaciones que la UI usa. La prueba de contraste las verifica todas contra AA. */
export const CONTRAST_PAIRS = [
    { foreground: 'foreground', background: 'background', use: 'text', note: 'Texto base' },
    { foreground: 'card-foreground', background: 'card', use: 'text', note: 'Texto en tarjetas' },
    {
        foreground: 'popover-foreground',
        background: 'popover',
        use: 'text',
        note: 'Diálogos y hojas',
    },
    { foreground: 'foreground', background: 'muted', use: 'text', note: 'Texto en zonas atenuadas' },
    {
        foreground: 'muted-foreground',
        background: 'background',
        use: 'text',
        note: 'Texto secundario',
    },
    {
        foreground: 'muted-foreground',
        background: 'card',
        use: 'text',
        note: 'Secundario en tarjeta',
    },
    { foreground: 'muted-foreground', background: 'muted', use: 'text', note: 'Secundario atenuado' },
    { foreground: 'primary-foreground', background: 'primary', use: 'text', note: 'Botón principal' },
    { foreground: 'primary', background: 'background', use: 'text', note: 'Enlaces y montos' },
    { foreground: 'primary', background: 'muted', use: 'text', note: 'Enlaces sobre atenuado' },
    {
        foreground: 'secondary-foreground',
        background: 'secondary',
        use: 'text',
        note: 'Botón secundario',
    },
    {
        foreground: 'accent-foreground',
        background: 'accent',
        use: 'text',
        note: 'Hover / seleccionado',
    },
    { foreground: 'highlight-foreground', background: 'highlight', use: 'text', note: 'CTA violeta' },
    { foreground: 'highlight', background: 'background', use: 'text', note: 'Texto violeta' },
    {
        foreground: 'highlight-soft-foreground',
        background: 'highlight-soft',
        use: 'text',
        note: 'Insignia informativa',
    },
    {
        foreground: 'destructive-foreground',
        background: 'destructive',
        use: 'text',
        note: 'Botón destructivo',
    },
    { foreground: 'destructive', background: 'background', use: 'text', note: 'Mensaje de error' },
    { foreground: 'success-foreground', background: 'success', use: 'text', note: 'Éxito sólido' },
    { foreground: 'success', background: 'background', use: 'text', note: 'Monto positivo' },
    {
        foreground: 'success-soft-foreground',
        background: 'success-soft',
        use: 'text',
        note: 'Éxito suave',
    },
    { foreground: 'warning-foreground', background: 'warning', use: 'text', note: 'Alerta sólida' },
    { foreground: 'warning', background: 'background', use: 'text', note: 'Texto de alerta' },
    {
        foreground: 'warning-soft-foreground',
        background: 'warning-soft',
        use: 'text',
        note: 'Alerta suave',
    },
    {
        foreground: 'danger-soft-foreground',
        background: 'danger-soft',
        use: 'text',
        note: 'Error suave',
    },
    { foreground: 'inverse-foreground', background: 'inverse', use: 'text', note: 'Superficie navy' },
    {
        foreground: 'inverse-muted-foreground',
        background: 'inverse',
        use: 'text',
        note: 'Secundario sobre navy',
    },
    {
        foreground: 'inverse-accent',
        background: 'inverse',
        use: 'largeText',
        note: 'Violeta claro sobre navy: solo títulos/cifras grandes e íconos',
    },
    { foreground: 'input', background: 'background', use: 'ui', note: 'Borde de campos' },
    { foreground: 'input', background: 'card', use: 'ui', note: 'Borde de campos en tarjeta' },
    { foreground: 'ring', background: 'background', use: 'ui', note: 'Anillo de foco' },
    { foreground: 'ring', background: 'card', use: 'ui', note: 'Foco en tarjeta' },
];
//# sourceMappingURL=theme.js.map